import {useEffect,useRef,useState} from 'react';
import SpecimenCabinet from './SpecimenCabinet';
import {getFloorEcology} from '../data/floorEcology';
import {getFuchiFloor} from '../data/fuchiFloors';

const towerReference='https://media-manager-c.questera.ai/greta-media/048a294ccd6718892c549bd7b643fcf120d7d84b38289b7b4b78889b4f78a748e8c921a45f072a6a0302366ee1557436/images/aW1hZ2UvanBlZw==/a9d6dbc22cd2cffb88abb92c3796704e.jpg';
const zoneDetails={
  Shallow: {label:'KAIYU · 1–29F'},
  Deep: {label:'SHINKAI · 31–59F'},
  Ancient: {label:'KO-HO · 61–89F'},
  Mythical: {label:'SHINWA · 91–100F'}
};

function getTowerMarkerPosition(floor) {
  const progress=(floor-1)/99;
  return 82-progress*62;
}

function FloorMarker({floor}) {
  return (
    <div
      className="tower-marker"
      style={{top:`${getTowerMarkerPosition(floor)}%`}}
      aria-hidden="true"
    >
      <i className="tower-marker__halo tower-marker__halo--outer" />
      <i className="tower-marker__halo tower-marker__halo--inner" />
      <span className="tower-marker__line tower-marker__line--left" />
      <span className="tower-marker__line tower-marker__line--right" />
      <b>{String(floor).padStart(3,'0')}</b>
    </div>
  );
}

function TowerHud({floor,lang,collapsed,setCollapsed}) {
  const data=getFuchiFloor(floor);
  const ecology=getFloorEcology(data);
  const details=zoneDetails[data.realm];
  const chinese=lang==='zh-Hant';

  return (
    <>
      <div className="tower-progress" aria-hidden="true">
        <div className="tower-progress__fill" style={{height:`${floor}%`}} />
        <i className="tower-progress__dot" style={{top:`${floor}%`}} />
        <span>SCROLL · DESCEND · {String(floor).padStart(3,'0')}/100</span>
      </div>
      <aside
        className={`tower-floor-panel ${collapsed ? 'is-collapsed' : ''}`}
        aria-live="polite"
      >
        <button
          className="tower-floor-panel__toggle"
          type="button"
          onClick={()=> setCollapsed((current)=> !current)}
          aria-expanded={!collapsed}
          aria-label={
            collapsed
              ? chinese ? '展開樓層資訊' : 'Expand floor information'
              : chinese ? '收合樓層資訊' : 'Collapse floor information'
          }
        >
          <span>
            {collapsed
              ? chinese ? '展開資訊' : 'EXPAND'
              : chinese ? '收合' : 'COLLAPSE'}
          </span>
          <b>{collapsed ? '＋' : '−'}</b>
        </button>
        <div className="tower-floor-panel__compact">
          <small>YOU ARE HERE</small>
          <strong>{String(floor).padStart(3,'0')}</strong>
          <em>FLOOR / 100</em>
        </div>
        <div className="tower-floor-panel__content">
          <div className="tower-floor-panel__here">
            YOU ARE HERE · {String(floor).padStart(3,'0')}F
          </div>
          <strong>{String(floor).padStart(3,'0')}</strong>
          <small>FLOOR / 100</small>
          <h3>{chinese ? data.name_zh : data.name_en}</h3>
          <p className="tower-floor-panel__english">
            {chinese ? data.name_en : data.realm}
          </p>
          <div className="tower-floor-panel__body">
            <span>CURRENT FLOOR</span>
            <b>{details.label}</b>
            <p>{chinese ? data.flavor_text_zh : data.flavor_text_en}</p>
            <div className="tower-floor-panel__tags">
              {ecology.environment.map((feature)=> (
                <em key={feature}>{feature}</em>
              ))}
            </div>
            <small>PROGRESS · {floor}/100 FLOORS DESCENDED</small>
          </div>
          <SpecimenCabinet floor={floor} embedded lang={lang} />
        </div>
      </aside>
    </>
  );
}

export default function FuchiTower({lang='zh-Hant'}) {
  const rootRef=useRef(null);
  const frameRef=useRef();
  const [floor,setFloor]=useState(1);
  const [collapsed,setCollapsed]=useState(false);

  useEffect(()=> {
    const updateProgress=()=> {
      frameRef.current=undefined;
      const section=rootRef.current?.closest('.abyss-section');
      if (!section) return;
      const rect=section.getBoundingClientRect();
      const distance=Math.max(1,section.offsetHeight-window.innerHeight);
      const progress=Math.min(1,Math.max(0,-rect.top/distance));
      const nextFloor=Math.min(100,Math.floor(progress*99)+1);
      setFloor((current)=> current===nextFloor ? current : nextFloor);
    };

    const requestUpdate=()=> {
      if (!frameRef.current) {
        frameRef.current=requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener('scroll',requestUpdate,{passive:true});
    window.addEventListener('resize',requestUpdate);

    return ()=> {
      window.removeEventListener('scroll',requestUpdate);
      window.removeEventListener('resize',requestUpdate);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  },[]);

  return (
    <div ref={rootRef} className="fuchi-tower fuchi-tower--reference">
      <style>{`
        .fuchi-tower--reference {position:relative;width:100%;height:100%;overflow:hidden;isolation:isolate;background: radial-gradient(ellipse at 50% 47%,#182431 0%,#07090d 48%,#010102 82%);}
        .fuchi-tower--reference::before {content:"";position:absolute;z-index:1;inset:0;pointer-events:none;background:linear-gradient(90deg,#010102d9 0%,transparent 22% 78%,#010102d9 100%),linear-gradient(180deg,#010102ba 0%,transparent 18% 82%,#010102db 100%);}
        .fuchi-tower__art {position:absolute;z-index:0;top:50%;left:50%;width:min(100%,620px);height:100%;object-fit:contain;object-position:center center;transform:translate(-50%,-50%);filter:saturate(.9) contrast(1.08) brightness(.84);user-select:none;pointer-events:none;}
        .fuchi-tower__art-frame {position:absolute;z-index:2;top:50%;left:50%;width:min(100%,620px);height:100%;transform:translate(-50%,-50%);pointer-events:none;background:radial-gradient(ellipse at center,transparent 46%,rgba(1,1,2,.12) 70%,rgba(1,1,2,.78) 100%);}
        .fuchi-heading {position:absolute;z-index:6;top:11vh;left:6vw;width:min(450px,45vw);pointer-events:none;}
        .fuchi-heading span {color:#a9362b;font:500 .68rem "Cormorant Garamond";letter-spacing:.34em;}
        .fuchi-heading h2 {margin:.8rem 0;color:#f0e5cf;font:500 clamp(2rem,4.4vw,4.7rem) "Noto Serif JP";text-shadow:0 5px 24px #000;}
        .fuchi-heading p {color:#aaa08d;line-height:1.8;text-shadow:0 2px 12px #000;}
        .tower-marker {position:absolute;z-index:4;left:50%;width:min(67vw,420px);height:70px;transform:translate(-50%,-50%);transition:top .48s cubic-bezier(.2,.8,.2,1);pointer-events:none;}
        .tower-marker__halo {position:absolute;top:50%;left:50%;border:1px solid rgba(255,217,119,.95);border-radius:50%;transform:translate(-50%,-50%) rotateX(67deg);box-shadow:0 0 13px #f4ba3f,0 0 35px rgba(238,170,39,.52);}
        .tower-marker__halo--outer {width:70%;height:50px;animation:orbit-glow 2.6s ease-in-out infinite;}
        .tower-marker__halo--inner {width:52%;height:34px;border-color:rgba(255,246,194,.88);animation:orbit-glow 2.6s .3s ease-in-out infinite reverse;}
        .tower-marker__line {position:absolute;top:50%;width:17%;height:1px;background:linear-gradient(90deg,transparent,#ffe3a1);box-shadow:0 0 10px #eeb13c;}
        .tower-marker__line--left {left:0;}
        .tower-marker__line--right {right:0;transform:rotate(180deg);}
        .tower-marker b {position:absolute;top:50%;left:50%;padding:.22rem .45rem;transform:translate(-50%,-50%);color:#fff3cb;border:1px solid rgba(255,223,143,.72);background:rgba(16,11,5,.85);box-shadow:0 0 19px #edb1378a;font:600 .7rem "Cormorant Garamond";letter-spacing:.12em;}
        .tower-progress {position:absolute;z-index:7;top:50%;left:20px;width:2px;height:min(60vh,580px);transform:translateY(-50%);background:rgba(255,255,255,.16);}
        .tower-progress__fill {position:absolute;top:0;width:100%;min-height:8px;background:linear-gradient(#a7211c,#d8a62d 56%,#f7dc82);box-shadow:0 0 14px #e8be54;transition:height .35s ease;}
        .tower-progress__dot {position:absolute;left:-5px;width:12px;height:12px;border:2px solid #fff6cf;border-radius:50%;background:#d7a83e;box-shadow:0 0 19px #f5c558;transform:translateY(-50%);transition:top .35s ease;}
        .tower-progress span {position:absolute;top:-5px;left:16px;color:#d2c4a8;font:500 .58rem "Cormorant Garamond";letter-spacing:.26em;white-space:nowrap;writing-mode:vertical-rl;}
        .tower-floor-panel {position:absolute;z-index:7;top:50%;right:5vw;width:min(350px,31vw);max-height:86vh;padding:1.35rem 1.45rem;overflow-x:hidden;overflow-y:auto;transform:translateY(-50%);border:1px solid rgba(228,190,105,.54);border-radius:15px;color:#eee4cb;background:linear-gradient(145deg,rgba(39,31,23,.94),rgba(6,7,9,.95));box-shadow:0 20px 55px #000b,inset 0 0 30px #d39d2614;backdrop-filter:blur(14px);transition:width .42s ease,padding .42s ease,background .42s ease;scrollbar-color:#967036 transparent;scrollbar-width:thin;}
        .tower-floor-panel__toggle {position:absolute;z-index:3;top:.7rem;right:.7rem;display:inline-flex;align-items:center;gap:.45rem;padding:.38rem .55rem;border:1px solid rgba(228,190,105,.4);border-radius:999px;color:#efd98e;background:rgba(8,7,6,.72);cursor:pointer;font:500 .53rem "Noto Serif JP";letter-spacing:.08em;}
        .tower-floor-panel__compact {display:none;}
        .tower-floor-panel__here {position:absolute;top:-2.25rem;left:0;padding:.45rem .8rem;border:1px solid #e5c66f;border-radius:999px;color:#fff0b5;background:#171310;box-shadow:0 0 18px #d29d2d26;font:600 .57rem "Cormorant Garamond";letter-spacing:.14em;white-space:nowrap;}
        .tower-floor-panel__content > strong {display:block;color:#f5da8b;font:600 clamp(3.2rem,5vw,5.4rem)/.8 "Cormorant Garamond";letter-spacing:.03em;}
        .tower-floor-panel__content > small,.tower-floor-panel__body > span {color:#a99c84;font:500 .57rem "Cormorant Garamond";letter-spacing:.22em;}
        .tower-floor-panel h3 {margin:1.1rem 0 .15rem;font:500 clamp(1.3rem,2vw,2rem) "Noto Serif JP";}
        .tower-floor-panel__english {color:#c9b27d;font:500 .62rem "Cormorant Garamond";letter-spacing:.18em;text-transform:uppercase;}
        .tower-floor-panel__body {display:grid;gap:.48rem;margin-top:1.15rem;padding-top:1rem;border-top:1px solid rgba(235,221,185,.18);}
        .tower-floor-panel__body b {color:#eed58e;font:600 .65rem "Cormorant Garamond";letter-spacing:.14em;}
        .tower-floor-panel__body p {color:#d1c6b2;font-size:.7rem;line-height:1.65;}
        .tower-floor-panel__body > small {margin-top:.55rem;color:#d1ae5e;}
        .tower-floor-panel__tags {display:flex;flex-wrap:wrap;gap:.35rem;margin-top:.25rem;}
        .tower-floor-panel__tags em {padding:.2rem .44rem;border:1px solid rgba(228,193,108,.46);border-radius:99px;color:#ead9ad;font:.55rem "Noto Serif JP";font-style:normal;letter-spacing:.06em;}
        .tower-floor-panel.is-collapsed {width:116px;max-height:none;padding:1rem .85rem .85rem;overflow:visible;background:linear-gradient(145deg,rgba(46,35,20,.95),rgba(6,7,9,.96));}
        .tower-floor-panel.is-collapsed .tower-floor-panel__content {display:none;}
        .tower-floor-panel.is-collapsed .tower-floor-panel__compact {display:grid;gap:.08rem;padding-top:2.1rem;text-align:center;}
        .tower-floor-panel__compact small,.tower-floor-panel__compact em {color:#b7a786;font:500 .46rem "Cormorant Garamond";font-style:normal;letter-spacing:.12em;}
        .tower-floor-panel__compact strong {color:#f7df94;font:600 2.65rem/.9 "Cormorant Garamond";}
        @keyframes orbit-glow {50% {opacity:.58;filter:brightness(1.45);transform:translate(-50%,-50%) rotateX(67deg) scale(1.07);}}
        @media (max-width:800px) {
          .fuchi-tower__art,.fuchi-tower__art-frame {width:100%;}
          .fuchi-tower__art {object-position:center center;}
          .fuchi-heading {top:13vh;left:7vw;width:84vw;}
          .fuchi-heading h2 {font-size:clamp(2rem,8vw,3.2rem);}
          .fuchi-heading p {display:none;}
          .tower-marker {width:86vw;}
          .tower-floor-panel {top:auto;right:5vw;bottom:5vh;width:min(272px,68vw);max-height:62vh;padding:1rem 1.05rem;transform:none;}
          .tower-floor-panel.is-collapsed {width:102px;padding:.8rem .65rem .7rem;}
          .tower-floor-panel__toggle {top:.45rem;right:.45rem;padding:.32rem .42rem;font-size:.46rem;}
          .tower-floor-panel__here {top:-1.95rem;font-size:.5rem;}
          .tower-floor-panel__content > strong {font-size:3.3rem;}
          .tower-floor-panel h3 {margin-top:.65rem;font-size:1.2rem;}
          .tower-floor-panel__body {margin-top:.72rem;padding-top:.72rem;}
          .tower-floor-panel__body p {font-size:.62rem;}
          .tower-progress {left:14px;height:42vh;}
          .tower-progress span {font-size:.5rem;}
        }
      `}</style>
      <img
        className="fuchi-tower__art"
        src={towerReference}
        alt=""
        aria-hidden="true"
      />
      <div className="fuchi-tower__art-frame" />
      <FloorMarker floor={floor} />
      <div className="fuchi-heading">
        <span>03 · SHUN NO FUCHI</span>
        <h2>
          {lang==='en'
            ? 'What Awaits Across The 100 Floors Of Shun no Fuchi?'
            : '旬之淵的百層之下藏著什麼？'}
        </h2>
        <p>
          {lang==='en'
            ? 'One hundred distinct oke universes,preserving one hundred memories beneath the water.'
            : '一百個不同的壽司桶宇宙，一百段被水保存的記憶。'}
        </p>
      </div>
      <TowerHud
        floor={floor}
        lang={lang}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
    </div>
  );
}